import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import type { TemplateResult } from 'lit';
import { getInstitutionByID } from '../api/institution.js';
import { getProductsByIDs } from '../api/product.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getParticipationsByID } from '../api/participation-helper.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import type { Einzelberatung } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import type { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { generateICSAttachment } from '../mails/generate-ics-attachment.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

defineMailTemplate(MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Bestätigung der Veranstaltungsregistrierung',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank f&uuml;r Ihre Anmeldung zu den Veranstaltungen.</p>
<p>{{changed-registrations}}</p>
<p>Unter <strong>"Mein Profil" </strong>k&ouml;nnen Sie sich von Veranstaltungen abmelden und auch zus&auml;tzliche Veranstaltungen buchen.</p>
<p>Bitte beachten Sie den Status Ihrer Anmeldung:</p>
<ul>
<li>angemeldet: Sie haben einen festen Platz auf der Teilnehmerliste</li>
<li>Warteliste: Halten Sie den Termin frei, da Sie m&ouml;glicherweise nachr&uuml;cken oder Sie kommen auf gut Gl&uuml;ck zum Workshop und erhalten einen Platz, weil angemeldete Studierende unentschuldigt fernbleiben.&nbsp;</li>
<li>abgemeldet: Ihr Platz wurde an einen anderen Teilnehmer vergeben.</li>
</ul>
<p><strong>Sie k&ouml;nnen nicht teilnehmen? Bitte abmelden und dadurch anderen Studierenden die Teilnahme erm&ouml;glichen</strong></p>
<p>Sollten Sie den Termin nicht wahrnehmen k&ouml;nnen, melden Sie sich bitte umgehend in der Anmeldeverwaltung ab. Damit erm&ouml;glichen Sie Studierenden auf der Warteliste die Teilnahme.</p>
<p>Beachten Sie, dass eine Abmeldung bis sp&auml;testens 6 Tage vor Workshop-Beginn erforderlich ist. Danach ist eine Abmeldung nur per E-Mail und mit triftigem Grund m&ouml;glich. Fehlen ohne Absage kann zu einem Ausschluss aus zuk&uuml;nftigen Workshops und zur Berechnung entstandener Kosten f&uuml;hren.</p>
<p>Wir w&uuml;nschen Ihnen viel Freude und Erkenntnisgewinn in den Veranstaltungen. Das ist unser Ansporn und Anspruch! Kontaktieren Sie uns gern <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a>, wenn Sie ein Anliegen (Fragen, Lob &amp; Kritik) haben.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Thank you for registering for the events.</p>
<p>{{changed-registrations}}</p>
<p>At <strong>"My Profile" </strong>you can unsubscribe from events and also book additional events.</p>
<p>Please bear in mind the status of your registration:</p>
<ul>
<li>Registered: You have a fixed place on the list of participants</li>
<li>&nbsp;Waiting list: Block the date, as you may be moved up the list, or you may come to the workshop by chance and get a seat because registered students are absent without excuse.</li>
<li>Deregistered: your spot was given to another participant.</li>
</ul>
<p>You are not able to attend? Please deregister and thereby allow other students to attend.</p>
<p>If you are unable to attend the scheduled event, please promptly unregister through the enrollment system. This will allow students on the waiting list to participate.</p>
<p>Note that you must unregister no later than 6 days before the workshop starts. After this deadline, unregistration is only possible via email and for valid reasons. Failure to attend without prior notice may result in exclusion from future workshops and the incurrence of associated costs.</p>
<p>We hope you will enjoy the events and gain a lot of knowledge. That is our incentive and claim! Feel free to contact us by e-mail if you have any concerns (questions, praise &amp; criticism).</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG),
  attachments: [],
  displayName: 'Workshop-Registrierung Bestätigung',
  editorHint: () => institutionMailHint(MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG),
});

export async function notifyBestaetigungWorkshopRegistrierung(
  institutionID: string,
  user: NarrowedEntityData<
    User,
    {
      _id: 1;
      email: 1;
      title: 1;
      username: 1;
      firstName: 1;
      lastName: 1;
      whatsappNotification: 1;
      suspentions: { _id: 1; institutionID: 1 };
      whatsappPhone: 1;
    }
  >,
  _participations: NarrowedEntityData<Participation, { _id: 1 }>[],
) {
  const institution = await getInstitutionByID(institutionID)({
    ...selectionSet(() => Institution, 1),
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('could not find institution');

  const mailTemplate = (await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG,
  )(InstitutionMailTemplate.allFields as any)) as InstitutionMailTemplate;

  const participations = await getParticipationsByID(_participations.map(p => p._id))({
    _id: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
    timeslotID: 1,
  } as any);
  if (!participations) throw new Error('could not find other participations');

  const participationProducts = await getProductsByIDs(participations.map(p => p.productID as string))({
    _id: 1,
    name: 1,
    topic: 1,
    language: 1,
    slots: { _id: 1, start: 1, end: 1 },
    settings: { courseLocation: 1, inheritedCourseLocation: 1 },
    appointments: { start: 1, name: 1, end: 1, location: 1 },
    slotConfig: { start: 1, end: 1, slotDuration: 1 },
  });
  for (const p of participations) (p as any).product = participationProducts.find(prod => prod._id === p.productID);

  const icsAttachment = generateICSAttachment(
    participations
      .filter(p => p.state === ParticipationState.in || p.state === ParticipationState.waiting)
      .map(p => {
        const product = participationProducts.find(prod => prod._id === p.productID);
        const workshop = product as Workshop | undefined;
        return {
          name: workshop?.name ?? '',
          appointments: workshop?.appointments ?? [],
          courseLocation: workshop?.settings?.courseLocation,
          participationState: p.state as ParticipationState,
        };
      }),
  );

  const start = (participations[0]?.product as Workshop)?.appointments?.[0]?.start;
  const daysTillEvent = start ? differenceInDays(start, new Date()).toFixed(0) : undefined;
  let dates = (participations[0]?.product as Maybe<Workshop>)?.appointments
    ?.map(a => (a.start ? format(a.start, 'dd.MM.yyyy') : null))
    .filter(isDefined)
    .join(', ');
  if (!dates) dates = start ? format(start, 'dd.MM.yyyy') : undefined;

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const otherParticipationData = (await loadMongoData(Participation, { userID: user._id })).filter(
    p => !_participations.map(_p => _p._id).includes(p._id),
  );
  const otherParticipations = await getParticipationsByID(otherParticipationData.map(p => p._id))({
    _id: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
  });
  if (!otherParticipations) throw new Error('could not find other participations');

  const otherProducts = await getProductsByIDs(otherParticipations.map(p => p.productID as string))({
    _id: 1,
    name: 1,
    appointments: { name: 1, start: 1, end: 1, location: 1 },
    hasPrice: 1,
    topic: 1,
    language: 1,
  });

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings.colorDefinitions ?? institution?.settings.inheritedColorDefinitions?.value;

  for (const p of otherParticipations) (p as any).product = otherProducts.find(p2 => p.productID === p2._id);

  const mailVars = {
    [MailTemplateVars.EVENT_TITLE]:
      participations
        .map(p => p?.product?.name)
        ?.filter(isDefined)
        ?.join(', ') ?? '',
    [MailTemplateVars.PRODUCT_NAME]:
      participations
        .map(p => p?.product?.name)
        ?.filter(isDefined)
        ?.join(', ') ?? '',
    [MailTemplateVars.EVENT_DATES]: dates ?? '',
    [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML(
      participations
        .toSorted((a, b) => {
          const a_start =
            (a.product as Workshop).appointments?.at(0)?.start || (a.product as Einzelberatung).slotConfig?.start;
          const b_start =
            (b.product as Workshop).appointments?.at(0)?.start || (b.product as Einzelberatung).slotConfig?.start;
          if (!a_start && !b_start) return 0;
          if (!a_start) return -1;
          if (!b_start) return 1;
          return a_start.getTime() - b_start.getTime();
        })
        .map(participation => ({
          name: participation.product!.name ?? '',
          state: participation.state,
          topic: participation.product!.topic,
          language: participation.product!.language,
          waitingListPlace: participation.waitingListPlace,
          appointments: (participation.product as Workshop)?.appointments || [],
          englishVersion: false,
        })),
    ),
    [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML(
      participations.map(participation => ({
        name: participation.product?.name ?? '',
        state: participation.state,
        topic: participation.product?.topic,
        language: participation.product?.language,
        waitingListPlace: participation.waitingListPlace,
        appointments: (participation.product as Workshop)?.appointments || [],
        englishVersion: true,
      })),
    ),
    [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
      participations: otherParticipations,
      institution,
      products: otherProducts,
      englishVersion: false,
    }),
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
  };

  const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

  if (!content) throw new Error('mail template has no content!');
  if (!subject) throw new Error('mail template has no subject!');

  const sendEmail = async () => {
    await sendInstitutionEmail({
      email: user.email,
      subject,
      content: content as TemplateResult<1>,
      institutionID,
      ...(icsAttachment ? { attachments: [icsAttachment] } : {}),
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institutionID);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        participations.map(participation => participation.product?.name).join(', ') || '-',
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    await sendEmail();
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
